import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Zap } from 'lucide-react';
import api from '@/services/api';
import { CountdownTimer } from './CountdownTimer';
import { formatCurrency } from '@/utils';

interface FlashDeal {
  id: string;
  discountPercent: number;
  endTime: string;
  product: {
    id: string;
    name: string;
    slug: string;
    images: string[];
    price: number;
    discountedPrice?: number;
    stock: number;
  };
}

export const FlashDealsSection = () => {
  const [deals, setDeals] = useState<FlashDeal[]>([]);

  useEffect(() => {
    api.get('/flash-deals').then(r => setDeals(r.data.data || [])).catch(() => {});
  }, []);

  if (deals.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-5">
        <div className="flex items-center gap-2 bg-red-500 text-white px-3 py-1.5 rounded-xl">
          <Zap className="h-4 w-4 fill-white" />
          <span className="font-heading font-bold text-sm">Flash Deals</span>
        </div>
        <span className="text-gray-400 text-sm">Limited time offers</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {deals.map(deal => {
          const flashPrice = deal.product.price * (1 - deal.discountPercent / 100);
          const basePrice = deal.product.discountedPrice ?? deal.product.price;

          return (
            <Link
              key={deal.id}
              to={`/products/${deal.product.slug}`}
              className="bg-white rounded-2xl shadow-card overflow-hidden hover:shadow-lg transition-all hover:-translate-y-1 group border border-red-100"
            >
              <div className="relative">
                <img
                  src={deal.product.images[0] || '/placeholder-product.jpg'}
                  alt={deal.product.name}
                  className="w-full h-36 object-contain p-3 bg-gray-50"
                />
                <div className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-lg">
                  -{deal.discountPercent}%
                </div>
                {deal.product.stock <= 5 && deal.product.stock > 0 && (
                  <div className="absolute top-2 right-2 bg-orange-100 text-orange-700 text-xs font-semibold px-1.5 py-0.5 rounded">
                    {deal.product.stock} left
                  </div>
                )}
              </div>
              <div className="p-3">
                <p className="text-xs font-semibold text-gray-800 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors">
                  {deal.product.name}
                </p>
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="font-bold text-red-600 text-sm">{formatCurrency(flashPrice)}</span>
                  <span className="text-xs text-gray-400 line-through">{formatCurrency(basePrice)}</span>
                </div>
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <span>Ends in:</span>
                  <CountdownTimer endTime={deal.endTime} onExpire={() => setDeals(prev => prev.filter(d => d.id !== deal.id))} />
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
};
